import React, { useEffect, useState } from "react";
import { OpForm } from "components/customAntd/DLS/OpForm/OpForm";
import { OpRow } from "components/customAntd/DLS/OpRow/OpRow";
import { OpCol } from "components/customAntd/DLS/OpCol/OpCol";
import Loader from "components/customAntd/Loader";
import dayjs from "dayjs";
import {
  getStatusNameById,
  getVisitDateTime,
  getStatusColor,
} from "utils/visitorsHelper";
import { DATE_TIME_AM_PM_FORMAT } from "constants/dates";
import STATUS from "constants/status";
import { formatFullName, statusKeyMap } from "utils/utils";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "store/store";
import { getRequest } from "api/apiClient";
import { FormFieldWorkflow, VisitData } from "types/formFieldTypes";
import { OpDivider } from "components/customAntd/DLS/OpDivider/OpDivider";
import { useTranslation } from "react-i18next";
import { fetchInvitationCustomFields } from "store/slices/visitorInvitationSlice";

const DetailsTab: React.FC = () => {
  const dispatch: AppDispatch = useDispatch();

  const orgId = useSelector((state: RootState) => state.globalOrg.globalOrgId);
  const globalLocationWorkflow = useSelector(
    (state: RootState) => state.locations.globalLocationWorkflow,
  );

  const { selectedVisit } = useSelector((state: RootState) => state.visits);
  const { invitationCustomFields } = useSelector(
    (state: RootState) => state.visitorInvitation,
  );

  const [statusColor, setStatusColor] = useState("transparent");
  const [customFieldsWorkflow, setCustomFieldsWorkflow] = useState<
    FormFieldWorkflow[]
  >([]);
  const [visitData, setVisitData] = useState<VisitData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const status = getStatusNameById(
    selectedVisit?.visitStatus.id!,
    selectedVisit?.scheduleStart,
  );

  const { t } = useTranslation();

  useEffect(() => {
    setStatusColor(getStatusColor(status));
    // eslint-disable-next-line
  }, [selectedVisit]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      try {
        const locationDefaultWorkflow = await getRequest(
          `/orgs/${orgId}/sites/${selectedVisit?.site?.id}/workflows`,
        );
        const locationVisitorTypeWorkflow = await getRequest(
          `/orgs/${orgId}/sites/${selectedVisit?.site.id}/visitorType/${selectedVisit?.visitorType.id}`,
        );

        const defaultWorkflowData = locationDefaultWorkflow?.data?.[0];
        const visitorTypeWorkflowData =
          locationVisitorTypeWorkflow?.data?.[0]?.workflow;

        const signInWorkflowId =
          visitorTypeWorkflowData?.SignInWorkflowId !== -1
            ? visitorTypeWorkflowData.SignInWorkflowId
            : defaultWorkflowData?.SignInWorkflowId;

        const customFieldsWorkflow = await getRequest(
          `/orgs/${orgId}/visitorWorkflow/${signInWorkflowId}/fieldCustom`,
        );
        setCustomFieldsWorkflow(customFieldsWorkflow.data);

        await dispatch(fetchInvitationCustomFields({ orgId }));

        const visitData = await getRequest(
          `/orgs/${orgId}/visitor/${selectedVisit?.id}/visitorData`,
        );
        // const filteredVisitData = visitData.data.filter((visitData: VisitData) => visitData.formField.status === 1);
        setVisitData(visitData.data);
      } catch (error) {
        console.log("Failed to fetch data.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [
    orgId,
    globalLocationWorkflow?.SignInWorkflowId,
    invitationCustomFields.length,
    selectedVisit?.id,
    selectedVisit?.visitStatus.id,
    selectedVisit?.site.id,
    selectedVisit?.visitorType.id,
    dispatch,
  ]);

  const initialValues: { [key: string]: any } = {
    company: selectedVisit?.company,
    visitorType: t(
      selectedVisit?.visitorType?.languageCode ||
        selectedVisit?.visitorType?.name ||
        "",
    ),
    visitStatus: t(statusKeyMap[status] || status),
    dateTime:
      status === "Pending" || status === "No Show"
        ? ""
        : getVisitDateTime(selectedVisit, status, DATE_TIME_AM_PM_FORMAT),
    scheduleStart: selectedVisit?.scheduleStart
      ? dayjs(selectedVisit.scheduleStart).format(DATE_TIME_AM_PM_FORMAT)
      : undefined,
    scheduleEnd: selectedVisit?.scheduleEnd
      ? dayjs(selectedVisit.scheduleEnd).format(DATE_TIME_AM_PM_FORMAT)
      : undefined,
    site: selectedVisit?.site?.name,
    host: formatFullName(
      selectedVisit?.host.firstName || "",
      null,
      selectedVisit?.host.lastName || "",
    ),
    purpose: selectedVisit?.purpose,
    confirmReturnItems:
      selectedVisit?.confirmReturnItems !== null
        ? selectedVisit?.confirmReturnItems === 1
          ? t("Yes")
          : t("No")
        : undefined,
  };

  // Add visitData fields to initialValues
  visitData.forEach((data) => {
    const { formField, value } = data;
    initialValues[formField.id] = value;
  });

  const renderFormFields = () => {
    const isPendingOrNoShow =
      selectedVisit?.visitStatus.id === STATUS.PENDING.id ||
      selectedVisit?.visitStatus.id === 99; // no-show

    const fieldsToRender = isPendingOrNoShow
      ? Array.from(
          new Map(
            [...customFieldsWorkflow, ...invitationCustomFields].map((f) => [
              f.id,
              f,
            ]),
          ).values(),
        )
      : customFieldsWorkflow;

    const customFieldIds = fieldsToRender.map((field) => field.id);

    return (
      <>
        {/* Render invitationCustomFields or customFieldsWorkflow */}
        {fieldsToRender.map((formField, index) => {
          if (formField.values) {
            const options = formField.values.map((option) => ({
              label: option.display || option.name,
              value: option.value,
            }));

            return (
              <OpForm.Select
                key={formField.id}
                label={formField.name}
                name={formField.id}
                options={options}
              />
            );
          } else {
            return (
              <OpForm.TextAreaInput
                key={formField.id}
                label={formField.name}
                name={formField.id}
                rows={4}
              />
            );
          }
        })}

        {/* Render visitData fields that are not present in invitationCustomFields or customFieldsWorkflow */}
        {visitData
          .filter(
            (visitField) => !customFieldIds.includes(visitField.formField.id),
          ) // Fields not in invitationCustomFields or customFieldsWorkflow
          .map((visitField) => {
            const formField = visitField.formField;

            if (formField.values) {
              const options = formField.values.map((option) => ({
                label: option.display || option.name,
                value: option.value,
              }));

              return (
                <OpForm.Select
                  key={formField.id}
                  label={formField.name}
                  name={formField.id}
                  options={options}
                />
              );
            } else {
              return (
                <OpForm.Input
                  key={formField.id}
                  label={formField.name}
                  name={formField.id}
                />
              );
            }
          })}
      </>
    );
  };

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <OpForm
          onSubmit={() => {}}
          hasError={false}
          defaultButtons={false}
          initialValues={initialValues}
          isReadOnly={true}
        >
          <OpRow gutter={16}>
            <OpCol span={12}>
              <OpForm.Input
                label={t("visitors.details.company")}
                name="company"
                readOnly
              />
            </OpCol>
            <OpCol span={12}>
              <OpForm.Input
                label={t("visitors.details.visitorType")}
                name="visitorType"
                readOnly
              />
            </OpCol>
          </OpRow>

          <OpRow gutter={16}>
            <OpCol span={12}>
              <OpForm.Input
                label={t("visitors.details.status")}
                name="visitStatus"
                readOnly
                style={{
                  backgroundColor: statusColor,
                  color: "white",
                  WebkitTextFillColor: "white",
                  opacity: 1,
                }}
              />
            </OpCol>
            <OpCol span={12}>
              <OpForm.Input
                label={t("visitors.details.dateTime")}
                name="dateTime"
                readOnly
              />
            </OpCol>
          </OpRow>

          <OpRow gutter={16}>
            <OpCol span={12}>
              <OpForm.Input
                label={t("visitors.details.scheduleStart")}
                name="scheduleStart"
                readOnly
              />
            </OpCol>
            <OpCol span={12}>
              <OpForm.Input
                label={t("visitors.details.scheduleEnd")}
                name="scheduleEnd"
                readOnly
              />
            </OpCol>
          </OpRow>

          <OpRow gutter={16}>
            <OpCol span={12}>
              <OpForm.Input
                label={t("visitors.details.location")}
                name="site"
                readOnly
              />
            </OpCol>
            <OpCol span={12}>
              <OpForm.Input
                label={t("visitors.details.host")}
                name="host"
                readOnly
              />
            </OpCol>
          </OpRow>

          <OpForm.TextAreaInput
            label={t("visitors.details.purpose")}
            name="purpose"
            readOnly
            rows={4}
          />

          {(selectedVisit?.confirmReturnItems === 0 ||
            selectedVisit?.confirmReturnItems === 1) && (
            <OpForm.Input
              label={t("visitors.details.confirmReturnItems")}
              name="confirmReturnItems"
              readOnly
            />
          )}
          <OpDivider />
          {renderFormFields()}
          {/* {selectedVisit?.visitStatus.id === STATUS.PENDING.id ? undefined : (renderFormFields())} */}
        </OpForm>
      )}
    </>
  );
};

export default DetailsTab;
